import { useCallback, useContext, useEffect, useRef } from 'react';
import { LinkContext, LinkDispatchContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat/context';
import { useIsFullPageChat } from '@trinity/chat-components/components/Chat/hooks/useIsFullPageChat';
import { ChatHistoryManagerContext, ChatTabsContext } from '@trinity/chat-components/components/Chat.History/context';
import { useLoadChatInstance } from '@trinity/chat-components/components/Chat.History/hooks/index';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { FullScreenChatBasePathContext } from './contexts';
import { useLocation, useNavigate } from './RoutingProvider';

export function useMonitorChatIdentifier() {
  const initializedIdentifier = useRef<string>(null);

  const link = useContext(LinkContext);
  const dispatch = useContext(LinkDispatchContext);
  const stateDispatch = useContext(ChatStateDispatchContext);
  const historyManager = useContext(ChatHistoryManagerContext);
  const tabs = useContext(ChatTabsContext);
  const location = useLocation();
  const session = useContext(ActiveChatSessionContext);
  const navigate = useNavigate();

  const handleChatLoaded = useCallback(() => {
    dispatch({
      type: 'update-chat-initialized',
      payload: {
        error: false,
      },
    });
  }, [dispatch]);

  const handleChatLoadError = useCallback(() => {
    dispatch({
      type: 'state-reset',
      payload: {
        chatIdentifier: null,
        initialized: true,
        error: false,
      },
    });
  }, [dispatch]);

  const loadChat = useLoadChatInstance({
    onError: handleChatLoadError,
    onSuccess: handleChatLoaded,
  });

  const isFullPageChat = useIsFullPageChat();
  const fullScreenChatPath = useContext(FullScreenChatBasePathContext);

  // Updates the url to reflect the active session's chat identifier
  useEffect(() => {
    if (session?.identifier && link.initialized) {
      if (isFullPageChat) {
        navigate({
          pathname: `${fullScreenChatPath}/${session.identifier}`,
          search: location.search,
        }, {
          replace: true,
        });
      }
    }
  }, [fullScreenChatPath, isFullPageChat, navigate, location.search, link.initialized, session?.identifier]);

  // Loads chat instance one time from the first chat identifier
  useEffect(() => {
    if (!historyManager?.initialized || !link.chatIdentifier) return;

    if (!initializedIdentifier.current || initializedIdentifier.current !== link.chatIdentifier) {
      initializedIdentifier.current = link.chatIdentifier;

      if (link.chatIdentifier === session?.identifier) {
        handleChatLoaded();
        return;
      }

      const unsavedTab = tabs?.items.find(
        (tabItem) => tabItem.identifier === link.chatIdentifier && tabItem.id == null
      );
      if (unsavedTab) {
        stateDispatch({
          type: 'unsaved-chat-tab-loaded',
          payload: {
            chatInstanceIdentifier: unsavedTab.identifier,
          },
        });
        handleChatLoaded();
        return;
      }

      loadChat(link.chatIdentifier);
    }
  }, [
    handleChatLoaded,
    loadChat,
    historyManager?.initialized,
    link.chatIdentifier,
    session?.identifier,
    stateDispatch,
    tabs?.items,
  ]);
}